import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoachAttendanceSessionRow, CoachAttendanceStatus } from '@court-sync/shared';
import {
  COACH_ATTENDANCE_PRIVATE_LOOKBACK_DAYS,
  COACH_ATTENDANCE_DEFAULT_FILTER_STATUSES,
  COACH_ATTENDANCE_STATUSES,
  countMarkedStudentAttendance,
  getCoachSelfCheckInRosterBlockReason,
  COACH_SELF_CHECK_IN_NO_STUDENTS_MSG,
  COACH_SELF_CHECK_IN_REQUIRES_STUDENT_ATTENDANCE_MSG,
} from '@court-sync/shared';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { coachService } from '@/services/coach';
import { lessonService } from '@/services/lesson';
import { PageHeader } from '@/components/layout/PageHeader';
import { CoachSelfCheckInActions } from '@/components/coach/CoachSelfCheckInActions';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import {
  ClipboardCheck,
  Filter,
  Loader2,
  Search,
  TrendingUp,
  UserCheck,
  UserX,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const PAGE_SIZE = 20;

const STATUS_FILTER_LABELS: Record<CoachAttendanceStatus, string> = {
  attended: 'Attended',
  missed: 'Missed',
  cancelled: 'Cancelled',
  scheduled: 'Scheduled',
};

function coachAttendanceStatusBadge(status: CoachAttendanceStatus): {
  label: string;
  className: string;
} {
  switch (status) {
    case 'attended':
      return {
        label: 'Attended',
        className:
          'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
      };
    case 'missed':
      return {
        label: 'Missed',
        className: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        className:
          'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
      };
    default:
      return {
        label: 'Scheduled',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
      };
  }
}

function statusesEqual(a: CoachAttendanceStatus[], b: CoachAttendanceStatus[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((s) => setB.has(s));
}

export default function CoachAttendancePage() {
  const academyId = useCoachAcademyStore((s) => s.academyId);
  const academy = useCoachAcademyStore((s) => s.academy);
  const allowSelfCheckIn = academy?.allowCoachSelfAttendance === true;

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingInSessionId, setCheckingInSessionId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState<CoachAttendanceStatus[]>(
    () => [...COACH_ATTENDANCE_DEFAULT_FILTER_STATUSES]
  );
  const [summary, setSummary] = useState<{
    assignedSessions: number;
    attendedSessions: number;
    missedSessions: number;
    attendanceRate: number;
    reportedAbsences: number;
  } | null>(null);
  const [sessions, setSessions] = useState<CoachAttendanceSessionRow[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [rosterStatsBySessionId, setRosterStatsBySessionId] = useState<
    Record<string, { rosterCount: number; markedCount: number }>
  >({});

  const searchPending = useMemo(() => {
    const draft = searchInput.trim();
    const applied = searchQuery.trim();
    return draft !== applied;
  }, [searchInput, searchQuery]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchQuery((prev) => (prev === trimmed ? prev : trimmed));
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilters, academyId]);

  const load = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!academyId) return;
      if (!options?.silent) {
        setLoading(true);
      }
      try {
        const data = await coachService.getMyAttendanceSummary(academyId, {
          page,
          limit: PAGE_SIZE,
          search: searchQuery || undefined,
          statuses: statusFilters,
        });
        setSummary(data.summary);
        setSessions(data.sessions);
        setPagination(data.pagination);
      } catch {
        if (!options?.silent) {
          setSummary(null);
          setSessions([]);
          setPagination(null);
        }
      } finally {
        if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    [academyId, page, searchQuery, statusFilters]
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;

    if (!academyId || !allowSelfCheckIn || sessions.length === 0) {
      setRosterStatsBySessionId({});
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      const entries = await Promise.all(
        sessions.map(async (session) => {
          try {
            const res = await lessonService.getLessonEnrollments(session.lessonId, {
              academyId,
              sessionDate: session.sessionDate,
            });
            const rows = (res.data ?? []).filter(
              (r) => r.status === 'confirmed' || r.status === 'pending'
            );
            return [
              session.sessionId,
              {
                rosterCount: rows.length,
                markedCount: countMarkedStudentAttendance(
                  rows.map((r) => r.attendanceStatus ?? 'scheduled')
                ),
              },
            ] as const;
          } catch {
            return [session.sessionId, { rosterCount: 0, markedCount: 0 }] as const;
          }
        })
      );

      if (!cancelled) {
        setRosterStatsBySessionId(Object.fromEntries(entries));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [academyId, allowSelfCheckIn, sessions]);

  const fetchSessionRosterStats = useCallback(
    async (row: CoachAttendanceSessionRow) => {
      if (!academyId) {
        return { rosterCount: 0, markedCount: 0 };
      }
      const res = await lessonService.getLessonEnrollments(row.lessonId, {
        academyId,
        sessionDate: row.sessionDate,
      });
      const rows = (res.data ?? []).filter(
        (r) => r.status === 'confirmed' || r.status === 'pending'
      );
      const stats = {
        rosterCount: rows.length,
        markedCount: countMarkedStudentAttendance(
          rows.map((r) => r.attendanceStatus ?? 'scheduled')
        ),
      };
      setRosterStatsBySessionId((prev) => ({ ...prev, [row.sessionId]: stats }));
      return stats;
    },
    [academyId]
  );

  const handleCheckIn = useCallback(
    async (row: CoachAttendanceSessionRow, status: 'attended' | 'scheduled') => {
      if (!academyId) return;

      if (status === 'attended') {
        const cached = rosterStatsBySessionId[row.sessionId];
        const { rosterCount, markedCount } = cached ?? (await fetchSessionRosterStats(row));
        const blockReason = getCoachSelfCheckInRosterBlockReason(rosterCount, markedCount);
        if (blockReason === 'no_students') {
          toast.error(COACH_SELF_CHECK_IN_NO_STUDENTS_MSG);
          return;
        }
        if (blockReason === 'incomplete_attendance') {
          toast.error(COACH_SELF_CHECK_IN_REQUIRES_STUDENT_ATTENDANCE_MSG);
          return;
        }
      }

      setCheckingInSessionId(row.sessionId);
      try {
        await coachService.checkInSelfSession({
          academyId,
          lessonId: row.lessonId,
          sessionDate: row.sessionDate,
          sessionId: row.sessionId,
          status,
        });
        toast.success(status === 'attended' ? 'Checked in' : 'Check-in cleared');
        await load({ silent: true });
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to update check-in');
      } finally {
        setCheckingInSessionId(null);
      }
    },
    [academyId, load, rosterStatsBySessionId, fetchSessionRosterStats]
  );

  const hasActiveFilters = useMemo(() => {
    const defaultStatuses = !statusesEqual(statusFilters, COACH_ATTENDANCE_DEFAULT_FILTER_STATUSES);
    return Boolean(searchQuery.trim()) || defaultStatuses;
  }, [searchQuery, statusFilters]);

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilters([...COACH_ATTENDANCE_DEFAULT_FILTER_STATUSES]);
  };

  const toggleStatusFilter = (status: CoachAttendanceStatus, checked: boolean) => {
    setStatusFilters((prev) => {
      if (checked) {
        return prev.includes(status) ? prev : [...prev, status];
      }
      const next = prev.filter((s) => s !== status);
      return next.length > 0 ? next : [...COACH_ATTENDANCE_DEFAULT_FILTER_STATUSES];
    });
  };

  const paginationControls =
    pagination && pagination.totalPages > 1 ? (
      <div className="flex flex-col gap-3 border-t border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-sm text-muted-foreground">
          Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loading || pagination.page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loading || pagination.page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    ) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Attendance"
        help={
          <p>
            {academy?.name
              ? `Your session attendance at ${academy.name}`
              : 'Your session attendance summary'}
          </p>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <DashboardStatCard
            label="Attendance rate"
            value={`${summary.attendanceRate.toFixed(1)}%`}
            icon={<TrendingUp className="text-blue-600 dark:text-blue-400" aria-hidden />}
            iconBg="bg-blue-100 dark:bg-blue-900"
            iconColor="text-blue-600 dark:text-blue-400"
          />
          <DashboardStatCard
            label="Attended"
            value={summary.attendedSessions}
            icon={<UserCheck className="text-green-600 dark:text-green-400" aria-hidden />}
            iconBg="bg-green-100 dark:bg-green-900"
            iconColor="text-green-600 dark:text-green-400"
          />
          <DashboardStatCard
            label="Missed"
            value={summary.missedSessions}
            icon={<UserX className="text-amber-600 dark:text-amber-400" aria-hidden />}
            iconBg="bg-amber-100 dark:bg-amber-900"
            iconColor="text-amber-600 dark:text-amber-400"
          />
          <DashboardStatCard
            label="Reported absences"
            value={summary.reportedAbsences}
            icon={<ClipboardCheck className="text-violet-600 dark:text-violet-400" aria-hidden />}
            iconBg="bg-violet-100 dark:bg-violet-900"
            iconColor="text-violet-600 dark:text-violet-400"
          />
        </div>
      )}

      <Card className="overflow-hidden shadow-sm">
        <CardHeader className="space-y-4 border-b border-border bg-muted/20 px-4 py-4 sm:px-6">
          <div className="space-y-1">
            <CardTitle className="text-base font-semibold">Session history</CardTitle>
            <CardDescription className="text-sm">
              Sessions in currently active terms, camps, and private lessons from the past{' '}
              {COACH_ATTENDANCE_PRIVATE_LOOKBACK_DAYS} days. Past sessions without a check-in or
              staff mark count as missed. Upcoming scheduled sessions are hidden unless you include
              them in the status filter.
              {!allowSelfCheckIn && (
                <>
                  {' '}
                  Your academy manages coach attendance — contact staff if you have questions.
                </>
              )}
            </CardDescription>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by lesson name…"
                aria-label="Search sessions by lesson name"
                aria-busy={searchPending}
                autoComplete="off"
                spellCheck={false}
                className="h-9 w-full rounded-md border border-input bg-background py-2 pl-9 pr-9 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500"
              />
              {searchPending ? (
                <Loader2
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
                  aria-hidden
                />
              ) : searchInput ? (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="inline-flex shrink-0 items-center gap-2"
                >
                  <Filter className="h-4 w-4" aria-hidden />
                  Status
                  {hasActiveFilters && (
                    <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs text-white">
                      •
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-4" align="end">
                <div className="space-y-3">
                  <p className="text-sm font-medium text-foreground">Filter by status</p>
                  <div className="space-y-2">
                    {COACH_ATTENDANCE_STATUSES.map((status) => (
                      <div key={status} className="flex items-center gap-2">
                        <Checkbox
                          id={`coach-attendance-status-${status}`}
                          checked={statusFilters.includes(status)}
                          onCheckedChange={(checked) =>
                            toggleStatusFilter(status, checked === true)
                          }
                        />
                        <Label
                          htmlFor={`coach-attendance-status-${status}`}
                          className="cursor-pointer text-sm font-normal"
                        >
                          {STATUS_FILTER_LABELS[status]}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-full text-muted-foreground"
                    onClick={() =>
                      setStatusFilters([...COACH_ATTENDANCE_STATUSES])
                    }
                  >
                    Show all statuses
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {hasActiveFilters && (
              <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">Loading…</p>
          ) : sessions.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              {hasActiveFilters
                ? 'No sessions match your search or filters.'
                : 'No assigned sessions in this scope.'}
            </p>
          ) : (
            <>
              <Table aria-label="Coach attendance sessions" className="min-w-[640px]">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Date
                    </TableHead>
                    <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Lesson
                    </TableHead>
                    <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Type
                    </TableHead>
                    <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Time
                    </TableHead>
                    <TableHead className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Status
                    </TableHead>
                    {allowSelfCheckIn && (
                      <TableHead className="h-11 whitespace-nowrap px-4 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Check-in
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((row) => {
                    const rowLoading = checkingInSessionId === row.sessionId;
                    const rosterStats = rosterStatsBySessionId[row.sessionId];
                    const rosterStatsLoaded = rosterStats != null;
                    const badge = coachAttendanceStatusBadge(row.status);

                    return (
                      <TableRow key={row.sessionId} className="border-border hover:bg-muted/40">
                        <TableCell className="px-4 py-3 tabular-nums text-foreground">
                          {row.sessionDate}
                        </TableCell>
                        <TableCell className="px-4 py-3 font-medium text-foreground">
                          {row.lessonName}
                        </TableCell>
                        <TableCell className="px-4 py-3 capitalize text-foreground">
                          {row.lessonType}
                        </TableCell>
                        <TableCell className="px-4 py-3 tabular-nums text-foreground">
                          {row.startTime} – {row.endTime}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        </TableCell>
                        {allowSelfCheckIn && (
                          <TableCell className="px-4 py-3 text-right">
                            {rowLoading ? (
                              <Loader2
                                className="ml-auto h-4 w-4 animate-spin text-muted-foreground"
                                aria-hidden
                              />
                            ) : row.selfCheckInAvailable || row.checkedIn || row.selfCheckInLocked ? (
                              <div className="ml-auto flex justify-end">
                                <CoachSelfCheckInActions
                                  rosterCount={rosterStats?.rosterCount ?? 0}
                                  markedCount={rosterStats?.markedCount ?? 0}
                                  rosterStatsLoaded={rosterStatsLoaded}
                                  checkedIn={row.checkedIn ?? false}
                                  checkingIn={rowLoading}
                                  selfCheckInAvailable={row.selfCheckInAvailable ?? false}
                                  selfCheckInLocked={row.selfCheckInLocked ?? false}
                                  onCheckIn={() => handleCheckIn(row, 'attended')}
                                  onUndo={() => handleCheckIn(row, 'scheduled')}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {paginationControls}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
